import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import useGetVideoPreview from "../../hooks/useGetVideoPreview";
import endpoints from "../../define/endpoints";
import { BREAKPOINTS } from "../../../constants";
import { useMatchesMinWidth } from "../../hooks/useMatchesMinWidth";

const CustomVideoComponent = ({
    src,
    alt,
    autoPlay = false,
    mutedByDefault = true,
    activeIndex,
    videoIsPlaying = false,
    setVideoIsPlaying,
}) => {
    const videoRef = useRef(null);
    const isDeviceSmallerThanTablet = !useMatchesMinWidth(BREAKPOINTS.TABLET);
    const previewSrc = useGetVideoPreview(src);
    const fallbackImage = `${endpoints.bucket}images/generic-placeholder.jpg`;

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.muted = mutedByDefault;
        }
    }, [mutedByDefault]);

    // manage autoplay
    useEffect(() => {
        if (autoPlay) {
            videoRef.current?.play();
            setVideoIsPlaying(true);
        } else {
            videoRef.current?.pause();
            videoRef.current.currentTime = 0;
        }
    }, [autoPlay]);

    // stop video on slider change
    useEffect(() => {
        const video = videoRef.current;
        if (video && !video.paused) {
            video.pause();
            video.currentTime = 0;
            video.load();
        }
    }, [activeIndex]);

    // Listen to video if playing
    useEffect(() => {
        if (!setVideoIsPlaying) return;

        const videoElement = videoRef.current;
        const handlePlayEvent = () => setVideoIsPlaying(true);
        const handlePauseEvent = () => setVideoIsPlaying(false);

        if (videoElement) {
            videoElement.addEventListener("play", handlePlayEvent);
            videoElement.addEventListener("pause", handlePauseEvent);
        }

        return () => {
            if (videoElement) {
                videoElement.removeEventListener("play", handlePlayEvent);
                videoElement.removeEventListener("pause", handlePauseEvent);
            }
        };
    }, []);

    return (
        <div className="custom-video-component__wrapper">
            {videoRef ? (
                <video
                    ref={videoRef}
                    controls={true}
                    controlsList="nodownload"
                    poster={previewSrc}
                    src={src}
                    alt={alt}
                    autoPlay={autoPlay}
                    muted={mutedByDefault}
                    playsInline
                />) : (
                <img src={fallbackImage} alt="fallback" loading="lazy" />
            )}
        </div>
    );

};

CustomVideoComponent.propTypes = {
    src: PropTypes.string.isRequired,
    alt: PropTypes.string,
    autoPlay: PropTypes.bool,
    mutedByDefault: PropTypes.bool,
    activeIndex: PropTypes.number,
    videoIsPlaying: PropTypes.bool,
    setVideoIsPlaying: PropTypes.func,
};

CustomVideoComponent.defaultProps = {
    setVideoIsPlaying: () => { },
};

export default CustomVideoComponent;